/**
 * @fileoverview Hook personalizado para sincronização do carrinho.
 * Gerencia sincronização automática entre carrinho local e remoto.
 * 
 * @module useCartSync
 * @since 1.0.0
 */

import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadCartAsync, saveCartAsync } from '../store/slices/cartSlice';

/**
 * Hook para sincronização automática do carrinho.
 * Carrega carrinho na inicialização e salva automaticamente nas mudanças.
 * 
 * @returns {Object} Funções e estado de sincronização
 */
export const useCartSync = () => {
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { items: cartItems, isLoading, error, needsSync } = useSelector((state) => state.cart);

  /**
   * Carrega carrinho da sessão ativa se usuário autenticado
   */
  const loadRemoteCart = useCallback(() => {
    if (isAuthenticated && user?.email) {
      dispatch(loadCartAsync());
    }
  }, [dispatch, isAuthenticated, user?.email]);

  /**
   * Salva carrinho na sessão ativa se usuário autenticado
   */
  const saveRemoteCart = useCallback(() => {
    if (isAuthenticated && user?.email && needsSync) {
      // map: converte itens do carrinho para formato da API com tipos corretos e validação
      const cartData = cartItems
        .filter(item => item.id && item.preco != null && item.quantity > 0)
        .map(item => ({
          productId: String(item.id),
          quantity: Number(item.quantity) || 1,
          price: Number(item.preco) || 0,
          name: String(item.nome || ''),
          brand: String(item.marca || ''),
          image: String(item.imagem || ''),
          category: String(item.categoria || '')
        }));
      
      dispatch(saveCartAsync(cartData));
    }
  }, [dispatch, isAuthenticated, user?.email, cartItems, needsSync]);

  /**
   * Carrega carrinho na inicialização se usuário autenticado
   */
  useEffect(() => {
    loadRemoteCart();
  }, [loadRemoteCart]);

  /**
   * Sincroniza automaticamente quando há mudanças no carrinho
   * Evita sincronização durante logout
   */
  useEffect(() => {
    if (needsSync && isAuthenticated) {
      const timeoutId = setTimeout(() => {
        // Verificar novamente se ainda está autenticado antes de sincronizar
        if (isAuthenticated && user?.email) {
          saveRemoteCart();
        }
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [needsSync, saveRemoteCart, isAuthenticated, user?.email]);

  return {
    loadRemoteCart,
    saveRemoteCart,
    isLoading,
    error
  };
};